// Customer Complaints — Feb 2026.
//
// End-to-end 8D / CAPA workflow driven from a single record:
//   • Header (auto-generated Complaint No, Status, dates, part auto-fill)
//   • Defect details + photos
//   • D1 Team
//   • D2 Problem Definition (5W + When)
//   • Fishbone diagram (Man / Material / Machine / Method / Tool / Environment)
//   • Cause Analysis table (probable-cause dropdown + Effect: N.R / Responsible)
//   • Why-Why-Why (5 Whys) for each chosen cause row
//   • Corrective Action + Preventive Action
//   • D3 Initial Containment Actions
//   • D4 Root Cause · D5 Chosen/Verified · D6 Implement/Validate · D7 Prevent · D8 Closure

import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import SearchableSelect from "../components/SearchableSelect";
import { Plus, Eye, PencilSimple, Printer, Trash, X, MagnifyingGlass, Camera, Fish } from "@phosphor-icons/react";
import { API } from "../App";

const DEFECT_LOCATIONS = ["Inner Side", "Outer Side", "Bottom Side", "Top Side"];
const FISHBONE_CATS   = ["Man", "Material", "Machine", "Method", "Tool", "Environment"];
const STATUS_OPTIONS  = ["Open", "In Progress", "Closed"];

const STATUS_STYLE = {
  "Open":        "bg-[#FEE2E2] text-[#B91C1C]",
  "In Progress": "bg-[#FEF3C7] text-[#B45309]",
  "Closed":      "bg-[#DCFCE7] text-[#166534]",
};

const EMPTY = {
  complaint_no: "", status: "Open",
  complaint_date: new Date().toISOString().slice(0, 10),
  drawing_no: "", customer: "", rev_no: "",
  part_id: "", part_no: "",
  invoice_date: "", part_description: "",
  lot_qty: "", rejected_qty: "", rework_qty: "", replaced_qty: "",
  customer_rep_name: "",
  complaint_statement: "",
  defect: "", defect_type: "", process_name: "",
  process_owner: "", quality_supervisor: "",
  operator_supervisor: "", inspector_name: "",
  defect_locations: [],
  photos: [],
  // CAPA / 8D
  capa_notes: "",
  team_members: [""],
  problem_what: "", problem_where: "", problem_who: "",
  problem_how: "", problem_many: "", problem_when: "",
  cause_analysis: [],   // [{category, probable_cause, yes_no}]
  why_count: 3,         // Dynamic number of Why columns (3 by default, "+ Add Column" grows)
  why_analysis:   [],   // [{category, probable_cause, whys:[…N…]}]
  root_cause: "",       // Free-text confirmed root cause
  corrective_action: "", // legacy (single textarea) — kept for backward-compat load
  corrective_action_steps: [], // NEW: array of {text, done}
  preventive_action: "",
  ica_notes: "",
  customer_stock: [{ invoice_no: "", lot_qty: "", rejected_qty: "", rework_qty: "", replace_qty: "" }],
  segregated_qty: "",
  company_total_segregated_qty: "", company_rejected_qty: "", company_rework_qty: "",
};

export default function CustomerComplaints() {
  const [rows, setRows] = useState([]);
  const [parts, setParts] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [defects, setDefects] = useState([]);
  const [causes, setCauses] = useState([]);      // Probable cause master rows
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [editorOpen, setEditorOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [viewing, setViewing] = useState(null);

  // Add-cause modal
  const [addCauseOpen, setAddCauseOpen] = useState(false);
  const [addCauseCat, setAddCauseCat] = useState("Man");
  const [addCauseText, setAddCauseText] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cc, pr, ps, df, pc] = await Promise.all([
        axios.get(`${API}/customer-complaints`,     { skipPeriod: true }),
        axios.get(`${API}/parts`,                   { skipPeriod: true }),
        axios.get(`${API}/processes`,               { skipPeriod: true }),
        axios.get(`${API}/defect-master`,           { skipPeriod: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/probable-cause-master`,   { skipPeriod: true }).catch(() => ({ data: [] })),
      ]);
      setRows(cc.data || []);
      setParts(pr.data || []);
      setProcesses(ps.data || []);
      setDefects(df.data || []);
      setCauses(pc.data || []);
    } catch { toast.error("Failed to load complaints"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!editorOpen) return;
    const partId = form.part_id;
    const partNo = form.part_no;
    if (!partId && !partNo) { setInvoices([]); return; }
    const params = partId ? `part_id=${encodeURIComponent(partId)}`
                          : `part_no=${encodeURIComponent(partNo)}`;
    axios.get(`${API}/customer-complaints-lookup/invoices?${params}`, { skipPeriod: true })
      .then(r => setInvoices(r.data || []))
      .catch(() => setInvoices([]));
  }, [editorOpen, form.part_id, form.part_no]);

  const applyPart = (part_id) => {
    const p = parts.find(x => x.id === part_id);
    if (!p) return { part_id: "", part_no: "" };
    return {
      part_id: p.id,
      part_no: p.part_no || "",
      drawing_no: p.drawing_no || "",
      customer: p.customer || "",
      rev_no: p.revision_no || p.rev_no || "",
      part_description: p.part_name || p.description || "",
    };
  };

  const openNew  = () => { setForm(EMPTY); setEditingId(null); setEditorOpen(true); };
  const openEdit = async (id) => {
    try {
      const r = await axios.get(`${API}/customer-complaints/${id}`, { skipPeriod: true });
      const doc = r.data;
      // Refresh Drawing / Rev / Customer / Description from the current Part
      // Master so they always mirror the latest master values — even for
      // records saved before the master had those fields populated.
      const p = parts.find(x => x.id === doc.part_id);
      if (p) {
        doc.drawing_no       = p.drawing_no       || doc.drawing_no       || "";
        doc.rev_no           = p.revision_no      || p.rev_no             || doc.rev_no || "";
        doc.customer         = p.customer         || doc.customer         || "";
        doc.part_description = p.part_name        || p.description        || doc.part_description || "";
      }
      setForm({ ...EMPTY, ...doc, photos: doc.photos || [] });
      setEditingId(id);
      setEditorOpen(true);
    } catch { toast.error("Failed to open complaint"); }
  };
  const openView = async (id) => {
    try {
      const r = await axios.get(`${API}/customer-complaints/${id}`, { skipPeriod: true });
      setViewing(r.data);
      setViewerOpen(true);
    } catch { toast.error("Failed to load complaint"); }
  };

  const save = async () => {
    if (!form.complaint_date || !form.part_no) {
      toast.error("Complaint Date and Part No are required");
      return;
    }
    const toNum = (v) => v === "" || v == null ? null : Number(v);
    const payload = {
      ...form,
      lot_qty:      toNum(form.lot_qty),
      rejected_qty: toNum(form.rejected_qty),
      rework_qty:   toNum(form.rework_qty),
      replaced_qty: toNum(form.replaced_qty),
      segregated_qty: toNum(form.segregated_qty),
      company_total_segregated_qty: toNum(form.company_total_segregated_qty),
      company_rejected_qty:         toNum(form.company_rejected_qty),
      company_rework_qty:           toNum(form.company_rework_qty),
      team_members: (form.team_members || []).filter(m => (m || "").trim() !== ""),
      // Keep every cause_analysis row (auto-populated from master); the user's
      // yes_no selection is the value that matters.
      cause_analysis: (form.cause_analysis || []).map(r => ({
        category: r.category,
        probable_cause: r.probable_cause,
        yes_no: r.yes_no || "",
      })),
      why_analysis: (form.why_analysis || []).filter(
        r => (r.whys || []).some(w => (w || "").trim() !== "")
      ),
      // Corrective Action is now a checklist. Drop empty steps.
      corrective_action_steps: (form.corrective_action_steps || []).filter(s => (s.text || "").trim() !== "")
        .map(s => ({ text: (s.text || "").trim(), done: !!s.done })),
      // Blank out the legacy string once migrated to steps.
      corrective_action: (form.corrective_action_steps && form.corrective_action_steps.length > 0)
        ? "" : (form.corrective_action || ""),
      customer_stock: (form.customer_stock || []).filter(r =>
        [r.invoice_no, r.lot_qty, r.rejected_qty, r.rework_qty, r.replace_qty]
          .some(v => (v || "") !== "")
      ).map(r => ({
        invoice_no: r.invoice_no || "",
        lot_qty:      toNum(r.lot_qty),
        rejected_qty: toNum(r.rejected_qty),
        rework_qty:   toNum(r.rework_qty),
        replace_qty:  toNum(r.replace_qty),
      })),
    };
    try {
      if (editingId) {
        await axios.put(`${API}/customer-complaints/${editingId}`, payload, { skipPeriod: true });
        toast.success("Complaint updated");
      } else {
        const r = await axios.post(`${API}/customer-complaints`, payload, { skipPeriod: true });
        toast.success(`Complaint saved · ${r.data?.complaint_no || ""}`);
      }
      setEditorOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const doDelete = async (id) => {
    if (!window.confirm("Delete this complaint? This can't be undone.")) return;
    try {
      await axios.delete(`${API}/customer-complaints/${id}`, { skipPeriod: true });
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const doPrint = async (id) => {
    try {
      const r = await axios.get(`${API}/customer-complaints/${id}`, { skipPeriod: true });
      renderPrintWindow(r.data);
    } catch { toast.error("Failed to load for print"); }
  };

  const addPhotos = async (fileList) => {
    const files = Array.from(fileList).slice(0, 10 - (form.photos?.length || 0));
    const readers = files.map(f => new Promise((res, rej) => {
      const img = new Image();
      const fr = new FileReader();
      fr.onload = () => {
        img.onload = () => {
          const MAX = 900;
          const scale = Math.min(1, MAX / Math.max(img.width, img.height));
          const w = Math.round(img.width * scale);
          const h = Math.round(img.height * scale);
          const c = document.createElement("canvas");
          c.width = w; c.height = h;
          c.getContext("2d").drawImage(img, 0, 0, w, h);
          const url = c.toDataURL("image/jpeg", 0.72);
          res({ name: f.name, data_url: url, size: Math.round(url.length * 0.75) });
        };
        img.onerror = rej;
        img.src = fr.result;
      };
      fr.onerror = rej;
      fr.readAsDataURL(f);
    }));
    try {
      const out = await Promise.all(readers);
      setForm(f => ({ ...f, photos: [...(f.photos || []), ...out] }));
    } catch { toast.error("Failed to read image"); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      (r.complaint_no || "").toLowerCase().includes(q) ||
      (r.part_no || "").toLowerCase().includes(q) ||
      (r.customer || "").toLowerCase().includes(q) ||
      (r.defect || "").toLowerCase().includes(q) ||
      (r.complaint_statement || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  const partOptions = useMemo(() => parts.map(p => ({
    value: p.id,
    label: p.part_no,
    hint: [p.part_name, p.customer].filter(Boolean).join(" · "),
  })), [parts]);

  const causesByCategory = useMemo(() => {
    const m = {};
    for (const c of FISHBONE_CATS) m[c] = [];
    for (const c of causes) {
      if (m[c.category]) m[c.category].push(c);
    }
    return m;
  }, [causes]);

  const toggleLoc = (loc) => {
    setForm(f => {
      const cur = new Set(f.defect_locations || []);
      cur.has(loc) ? cur.delete(loc) : cur.add(loc);
      return { ...f, defect_locations: Array.from(cur) };
    });
  };

  const addProbableCause = async () => {
    const cause = addCauseText.trim();
    if (!cause) { toast.error("Enter the probable cause text"); return; }
    try {
      const r = await axios.post(`${API}/probable-cause-master`,
        { category: addCauseCat, cause }, { skipPeriod: true });
      setCauses(prev => {
        if (prev.find(x => x.id === r.data.id)) return prev;
        return [...prev, r.data];
      });
      // Cause Analysis table auto-syncs from the master list — no need to
      // push a row manually here.
      toast.success("Probable cause added");
      setAddCauseText("");
      setAddCauseOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add cause");
    }
  };

  // Handy: sync why_analysis rows with "Yes" (Effective) cause_analysis rows.
  // The Root Cause Analysis section only shows 5-Whys for causes marked "Yes".
  const responsibleCauses = (form.cause_analysis || []).filter(r => r.yes_no === "Yes");

  return (
    <Layout title="Customer Complaints">
      <div className="p-4 md:p-6 space-y-4 max-w-[1400px] mx-auto">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-black tracking-tight text-[#1D2951]" style={{ fontFamily: "Chivo" }}>
              Customer Complaints
            </h1>
            <p className="text-xs md:text-sm text-[#64748B] mt-0.5">
              Record customer-reported defects with 8D / CAPA, fishbone analysis and 5-Whys.
            </p>
          </div>
          <Button onClick={openNew} className="bg-[#1D2951] hover:bg-[#2A3B73] h-9" data-testid="cc-new-btn">
            <Plus size={14} className="mr-1.5" /> New Complaint
          </Button>
        </div>

        <Card className="p-3 border border-[#E2E8F0]">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by Complaint No / Part No / Customer / Defect"
              className="h-9 pl-7 text-sm" data-testid="cc-search" />
          </div>
        </Card>

        <Card className="border border-[#E2E8F0] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#1D2951] text-white">
                <tr>
                  <th className="px-3 py-3 text-left font-semibold">Complaint No</th>
                  <th className="px-3 py-3 text-left font-semibold">Date</th>
                  <th className="px-3 py-3 text-left font-semibold">Part No</th>
                  <th className="px-3 py-3 text-left font-semibold">Customer</th>
                  <th className="px-3 py-3 text-left font-semibold">Defect</th>
                  <th className="px-3 py-3 text-right font-semibold">Rej Qty</th>
                  <th className="px-3 py-3 text-center font-semibold">Status</th>
                  <th className="px-3 py-3 text-center font-semibold">Photos</th>
                  <th className="px-3 py-3 text-center font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1F5F9]">
                {loading && <tr><td colSpan={9} className="py-12 text-center text-[#64748B]">Loading…</td></tr>}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={9} className="py-16 text-center text-[#94A3B8]">
                    No complaints yet. Click <b>New Complaint</b> to record the first one.
                  </td></tr>
                )}
                {filtered.map(r => (
                  <tr key={r.id} className="hover:bg-[#F8FAFC]" data-testid={`cc-row-${r.id}`}>
                    <td className="px-3 py-2 font-mono font-bold text-[#1D2951]">{r.complaint_no || "—"}</td>
                    <td className="px-3 py-2 font-mono">{r.complaint_date || "—"}</td>
                    <td className="px-3 py-2 font-mono font-bold text-[#0F172A]">{r.part_no || "—"}</td>
                    <td className="px-3 py-2 text-[#334155]">{r.customer || "—"}</td>
                    <td className="px-3 py-2 text-[#475569]">{r.defect || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.rejected_qty ?? "—"}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${STATUS_STYLE[r.status || "Open"] || "bg-[#F1F5F9] text-[#64748B]"}`}>
                        {r.status || "Open"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {r.photo_count > 0 ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#1D2951]"><Camera size={12}/> {r.photo_count}</span> : <span className="text-[#94A3B8]">—</span>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-center gap-1">
                        <IconBtn title="View"   onClick={() => openView(r.id)}   testId={`cc-view-${r.id}`}   ><Eye size={14} /></IconBtn>
                        <IconBtn title="Edit"   onClick={() => openEdit(r.id)}   testId={`cc-edit-${r.id}`}   ><PencilSimple size={14} /></IconBtn>
                        <IconBtn title="Print"  onClick={() => doPrint(r.id)}    testId={`cc-print-${r.id}`}  ><Printer size={14} /></IconBtn>
                        <IconBtn title="Delete" onClick={() => doDelete(r.id)}   testId={`cc-delete-${r.id}`} danger><Trash size={14} /></IconBtn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* ── Editor dialog ── */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="cc-editor">
          <DialogHeader>
            <DialogTitle className="text-xl font-black text-[#1D2951] flex items-center gap-3">
              {editingId ? "Edit Complaint" : "New Customer Complaint"}
              {form.complaint_no && (
                <span className="text-[11px] font-mono font-bold px-2 py-1 rounded bg-[#D4A843] text-[#1D2951]">
                  {form.complaint_no}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Status + Header row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Complaint Status">
                <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger className="h-9" data-testid="cc-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Customer Complaint Date *">
                <Input type="date" value={form.complaint_date}
                  onChange={e => setForm(f => ({ ...f, complaint_date: e.target.value }))}
                  className="h-9" data-testid="cc-complaint-date" />
              </Field>
              <Field label="Invoice Date">
                <Input type="date" value={form.invoice_date}
                  onChange={e => setForm(f => ({ ...f, invoice_date: e.target.value }))}
                  className="h-9" data-testid="cc-invoice-date" />
              </Field>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Part No *" hint="Picks Drawing, Rev, Customer, Description automatically">
                <SearchableSelect options={partOptions} value={form.part_id}
                  onChange={(id) => setForm(f => ({ ...f, ...applyPart(id) }))}
                  placeholder="Pick a part no…" testId="cc-part" />
              </Field>
              <Field label="Part Description">
                <Input value={form.part_description}
                  onChange={e => setForm(f => ({ ...f, part_description: e.target.value }))}
                  className="h-9" data-testid="cc-part-desc" />
              </Field>
              <Field label="Drawing No" hint="Auto-filled from Part Master (read-only)">
                <Input value={form.drawing_no} readOnly disabled
                  className="h-9 bg-[#F1F5F9] cursor-not-allowed" data-testid="cc-drawing-no"
                  placeholder="— Set in Part Master —" />
              </Field>
              <Field label="Rev No" hint="Auto-filled from Part Master (read-only)">
                <Input value={form.rev_no} readOnly disabled
                  className="h-9 bg-[#F1F5F9] cursor-not-allowed" data-testid="cc-rev-no"
                  placeholder="— Set in Part Master —" />
              </Field>
              <Field label="Customer">
                <Input value={form.customer}
                  onChange={e => setForm(f => ({ ...f, customer: e.target.value }))}
                  className="h-9" data-testid="cc-customer" />
              </Field>
              <Field label="Lot Qty">
                <Input type="number" value={form.lot_qty}
                  onChange={e => setForm(f => ({ ...f, lot_qty: e.target.value }))}
                  className="h-9 font-mono" data-testid="cc-lot-qty" />
              </Field>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Rejected Qty">
                <Input type="number" value={form.rejected_qty}
                  onChange={e => setForm(f => ({ ...f, rejected_qty: e.target.value }))}
                  className="h-9 font-mono" data-testid="cc-rej-qty" />
              </Field>
              <Field label="Rework Qty">
                <Input type="number" value={form.rework_qty}
                  onChange={e => setForm(f => ({ ...f, rework_qty: e.target.value }))}
                  className="h-9 font-mono" data-testid="cc-rework-qty" />
              </Field>
              <Field label="Replaced Qty">
                <Input type="number" value={form.replaced_qty}
                  onChange={e => setForm(f => ({ ...f, replaced_qty: e.target.value }))}
                  className="h-9 font-mono" data-testid="cc-replaced-qty" />
              </Field>
            </div>

            <Field label="Customer Representative Name">
              <Input value={form.customer_rep_name}
                onChange={e => setForm(f => ({ ...f, customer_rep_name: e.target.value }))}
                className="h-9" data-testid="cc-rep-name" />
            </Field>

            <Field label="Complaint Statement">
              <Textarea rows={3} value={form.complaint_statement}
                onChange={e => setForm(f => ({ ...f, complaint_statement: e.target.value }))}
                placeholder="Describe the complaint as the customer reported it…"
                data-testid="cc-statement" />
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Defect" hint="Picks Type of Defect automatically from Defect Master">
                {defects.length > 0 ? (
                  <Select value={form.defect}
                    onValueChange={v => {
                      const d = defects.find(x => (x.name || x.code) === v);
                      setForm(f => ({ ...f, defect: v, defect_type: d?.defect_type || d?.type || f.defect_type }));
                    }}>
                    <SelectTrigger className="h-9" data-testid="cc-defect"><SelectValue placeholder="Pick / type…" /></SelectTrigger>
                    <SelectContent>
                      {defects.map(d => <SelectItem key={d.id || d.name} value={d.name || d.code}>{d.name || d.code}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={form.defect}
                    onChange={e => setForm(f => ({ ...f, defect: e.target.value }))}
                    className="h-9" data-testid="cc-defect" />
                )}
              </Field>
              <Field label="Type of Defect" hint="Auto-filled from Defect Master (editable)">
                <Input value={form.defect_type}
                  onChange={e => setForm(f => ({ ...f, defect_type: e.target.value }))}
                  className="h-9" data-testid="cc-defect-type" placeholder="Cosmetic / Dimensional / Functional…" />
              </Field>
              <Field label="Process Name">
                <Select value={form.process_name}
                  onValueChange={v => setForm(f => ({ ...f, process_name: v }))}>
                  <SelectTrigger className="h-9" data-testid="cc-process"><SelectValue placeholder="Pick process…" /></SelectTrigger>
                  <SelectContent>
                    {processes.map(p => <SelectItem key={p.code || p.id} value={p.name || p.code}>{p.name || p.code}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Process Owner">
                <Input value={form.process_owner}
                  onChange={e => setForm(f => ({ ...f, process_owner: e.target.value }))}
                  className="h-9" data-testid="cc-po" />
              </Field>
              <Field label="Quality Supervisor">
                <Input value={form.quality_supervisor}
                  onChange={e => setForm(f => ({ ...f, quality_supervisor: e.target.value }))}
                  className="h-9" data-testid="cc-qs" />
              </Field>
              <Field label="Operator Supervisor">
                <Input value={form.operator_supervisor}
                  onChange={e => setForm(f => ({ ...f, operator_supervisor: e.target.value }))}
                  className="h-9" data-testid="cc-os" />
              </Field>
              <Field label="Inspector Name">
                <Input value={form.inspector_name}
                  onChange={e => setForm(f => ({ ...f, inspector_name: e.target.value }))}
                  className="h-9" data-testid="cc-inspector" />
              </Field>
            </div>

            <Field label="Defect Location">
              <div className="flex flex-wrap gap-2">
                {DEFECT_LOCATIONS.map(loc => {
                  const on = (form.defect_locations || []).includes(loc);
                  return (
                    <button key={loc} type="button" onClick={() => toggleLoc(loc)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition
                        ${on ? "bg-[#1D2951] border-[#1D2951] text-white"
                             : "bg-white border-[#E2E8F0] text-[#334155] hover:border-[#1D2951]"}`}
                      data-testid={`cc-loc-${loc.replace(/\s+/g,'-').toLowerCase()}`}>
                      {loc}
                    </button>
                  );
                })}
              </div>
            </Field>

            <Field label={`Photos of Defect (${(form.photos || []).length}/10)`}>
              <div className="flex flex-wrap gap-2">
                {(form.photos || []).map((p, i) => (
                  <div key={i} className="relative w-24 h-24 rounded-lg overflow-hidden border border-[#E2E8F0]">
                    <img src={p.data_url} alt={p.name} className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setForm(f => ({ ...f, photos: f.photos.filter((_, j) => j !== i) }))}
                      className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5 text-[#EF4444] hover:bg-white"
                      data-testid={`cc-photo-remove-${i}`}>
                      <X size={12} weight="bold" />
                    </button>
                  </div>
                ))}
                {(form.photos || []).length < 10 && (
                  <label className="w-24 h-24 rounded-lg border-2 border-dashed border-[#CBD5E1] flex flex-col items-center justify-center cursor-pointer hover:border-[#1D2951] hover:bg-[#F8FAFC]"
                    data-testid="cc-photo-add">
                    <Camera size={20} className="text-[#94A3B8] mb-1" />
                    <span className="text-[10px] text-[#64748B] font-semibold">Add</span>
                    <input type="file" accept="image/*" multiple className="hidden"
                      onChange={e => addPhotos(e.target.files)} />
                  </label>
                )}
              </div>
            </Field>

            {/* ─────── CAPA / 8D ─────── */}
            <SectionDivider label="CAPA · 8D" />

            <Field label="CAPA Notes">
              <Textarea rows={2} value={form.capa_notes}
                onChange={e => setForm(f => ({ ...f, capa_notes: e.target.value }))}
                placeholder="Overall CAPA remarks / next steps…"
                data-testid="cc-capa-notes" />
            </Field>

            {/* D1 — Team */}
            <PhasePanel code="D1" title="Team">
              <div className="space-y-2">
                {(form.team_members || [""]).map((m, i) => (
                  <div key={i} className="flex gap-2">
                    <Input value={m}
                      onChange={e => setForm(f => {
                        const arr = [...(f.team_members || [""])];
                        arr[i] = e.target.value;
                        return { ...f, team_members: arr };
                      })}
                      placeholder={`Member ${i + 1} name`}
                      className="h-9 flex-1" data-testid={`cc-team-${i}`} />
                    {(form.team_members || []).length > 1 && (
                      <IconBtn title="Remove" onClick={() => setForm(f => ({
                        ...f, team_members: (f.team_members || []).filter((_, j) => j !== i)
                      }))} testId={`cc-team-rm-${i}`} danger><X size={14} /></IconBtn>
                    )}
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm"
                  onClick={() => setForm(f => ({ ...f, team_members: [...(f.team_members || []), ""] }))}
                  className="h-8" data-testid="cc-team-add">
                  <Plus size={12} className="mr-1" /> Add member
                </Button>
              </div>
            </PhasePanel>

            {/* D2 — Problem Definition (5W + When) */}
            <PhasePanel code="D2" title="Problem Definition">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  ["What",  "problem_what"],
                  ["Where", "problem_where"],
                  ["When",  "problem_when"],
                  ["Who",   "problem_who"],
                  ["How",   "problem_how"],
                  ["Many",  "problem_many"],
                ].map(([lbl, key]) => (
                  <Field key={key} label={lbl}>
                    <Input value={form[key] || ""}
                      onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                      className="h-9" data-testid={`cc-${key}`} />
                  </Field>
                ))}
              </div>
            </PhasePanel>

            {/* D3 — Initial Containment Actions */}
            <PhasePanel code="D3" title="Initial Containment Actions (ICA)">
              <Field label="ICA Notes">
                <Textarea rows={2} value={form.ica_notes}
                  onChange={e => setForm(f => ({ ...f, ica_notes: e.target.value }))}
                  placeholder="Containment steps taken to prevent shipment of suspect stock…"
                  data-testid="cc-ica-notes" />
              </Field>

              <div className="mt-3">
                <div className="text-[11px] font-bold uppercase tracking-widest text-[#64748B] mb-1">Customer Stock</div>
                <div className="overflow-x-auto rounded-lg border border-[#E2E8F0]">
                  <table className="w-full text-xs">
                    <thead className="bg-[#F1F5F9] text-[#1D2951]">
                      <tr>
                        <th className="px-2 py-2 text-left font-semibold">Invoice No</th>
                        <th className="px-2 py-2 text-right font-semibold">Lot Qty</th>
                        <th className="px-2 py-2 text-right font-semibold">Rejected Qty</th>
                        <th className="px-2 py-2 text-right font-semibold">Rework Qty</th>
                        <th className="px-2 py-2 text-right font-semibold">Replace Qty</th>
                        <th className="px-2 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F1F5F9]">
                      {(form.customer_stock || []).map((row, i) => {
                        const opts = invoices.map(inv => ({
                          value: inv.invoice_no,
                          label: inv.invoice_no,
                          hint: [inv.date, inv.qty ? `Qty ${inv.qty}` : ""].filter(Boolean).join(" · "),
                        }));
                        if (row.invoice_no && !opts.find(o => o.value === row.invoice_no)) {
                          opts.unshift({ value: row.invoice_no, label: row.invoice_no, hint: "(manual)" });
                        }
                        return (
                        <tr key={i}>
                          <td className="p-1 min-w-[200px]">
                            {invoices.length > 0 || row.invoice_no ? (
                              <SearchableSelect
                                options={opts}
                                value={row.invoice_no || ""}
                                onChange={(v) => setForm(f => {
                                  const arr = [...(f.customer_stock || [])];
                                  const inv = invoices.find(x => x.invoice_no === v);
                                  const nextLot = arr[i].lot_qty === "" && inv?.qty != null
                                    ? String(inv.qty) : arr[i].lot_qty;
                                  arr[i] = { ...arr[i], invoice_no: v, lot_qty: nextLot };
                                  return { ...f, customer_stock: arr };
                                })}
                                placeholder={form.part_no ? "Pick invoice…" : "Pick part first"}
                                testId={`cc-cs-invoice-${i}`}
                              />
                            ) : (
                              <Input value={row.invoice_no || ""}
                                onChange={e => setForm(f => {
                                  const arr = [...(f.customer_stock || [])];
                                  arr[i] = { ...arr[i], invoice_no: e.target.value };
                                  return { ...f, customer_stock: arr };
                                })}
                                placeholder={form.part_no ? "No invoices found — type manually" : "Pick part first"}
                                className="h-8 text-xs" data-testid={`cc-cs-invoice-${i}`} />
                            )}
                          </td>
                          {["lot_qty", "rejected_qty", "rework_qty", "replace_qty"].map(k => (
                            <td key={k} className="p-1">
                              <Input type="number" value={row[k] || ""}
                                onChange={e => setForm(f => {
                                  const arr = [...(f.customer_stock || [])];
                                  arr[i] = { ...arr[i], [k]: e.target.value };
                                  return { ...f, customer_stock: arr };
                                })}
                                className="h-8 text-xs font-mono text-right" data-testid={`cc-cs-${k}-${i}`} />
                            </td>
                          ))}
                          <td className="p-1 text-center">
                            {(form.customer_stock || []).length > 1 && (
                              <IconBtn title="Remove" onClick={() => setForm(f => ({
                                ...f, customer_stock: (f.customer_stock || []).filter((_, j) => j !== i)
                              }))} testId={`cc-cs-rm-${i}`} danger><X size={12} /></IconBtn>
                            )}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Button type="button" variant="outline" size="sm" className="mt-2 h-8"
                  onClick={() => setForm(f => ({
                    ...f, customer_stock: [...(f.customer_stock || []),
                      { invoice_no: "", lot_qty: "", rejected_qty: "", rework_qty: "", replace_qty: "" }]
                  }))}
                  data-testid="cc-cs-add">
                  <Plus size={12} className="mr-1" /> Add invoice row
                </Button>
              </div>

              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Segregated Qty (Customer)">
                  <Input type="number" value={form.segregated_qty}
                    onChange={e => setForm(f => ({ ...f, segregated_qty: e.target.value }))}
                    className="h-9 font-mono" data-testid="cc-segregated-qty" />
                </Field>
              </div>

              <div className="mt-4 pt-3 border-t border-[#E2E8F0]">
                <div className="text-[11px] font-bold uppercase tracking-widest text-[#64748B] mb-2">Company Stock</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Field label="Total Segregated Qty">
                    <Input type="number" value={form.company_total_segregated_qty}
                      onChange={e => setForm(f => ({ ...f, company_total_segregated_qty: e.target.value }))}
                      className="h-9 font-mono" data-testid="cc-co-segregated" />
                  </Field>
                  <Field label="Rejected Qty">
                    <Input type="number" value={form.company_rejected_qty}
                      onChange={e => setForm(f => ({ ...f, company_rejected_qty: e.target.value }))}
                      className="h-9 font-mono" data-testid="cc-co-rejected" />
                  </Field>
                  <Field label="Rework Qty">
                    <Input type="number" value={form.company_rework_qty}
                      onChange={e => setForm(f => ({ ...f, company_rework_qty: e.target.value }))}
                      className="h-9 font-mono" data-testid="cc-co-rework" />
                  </Field>
                </div>
              </div>
            </PhasePanel>
            {/* ── Fishbone Diagram ── */}
            <PhasePanel code="Fishbone" title="Ishikawa Cause Analysis" accent="#0EA5E9">
              <FishboneDiagram
                categories={FISHBONE_CATS}
                causesByCategory={causesByCategory}
                onAddCause={(cat) => { setAddCauseCat(cat); setAddCauseText(""); setAddCauseOpen(true); }}
              />
            </PhasePanel>

            {/* ── Cause Analysis Table (auto-populated from probable-cause master) ── */}
            <PhasePanel code="Analysis" title="Cause Analysis Table" accent="#0EA5E9">
              <CauseAnalysisTable
                rows={form.cause_analysis || []}
                causesByCategory={causesByCategory}
                onChange={(next) => setForm(f => ({ ...f, cause_analysis: next }))}
              />
            </PhasePanel>


            {/* D4 — Root Cause Analysis (dynamic-Whys table for rows marked "Yes") */}
            <PhasePanel code="D4" title="Root Cause Analysis" accent="#F59E0B">
              <WhyAnalysisTable
                responsibleCauses={responsibleCauses}
                rows={form.why_analysis || []}
                whyCount={form.why_count || 3}
                onChange={(next) => setForm(f => ({ ...f, why_analysis: next }))}
                onAddColumn={() => setForm(f => ({ ...f, why_count: (f.why_count || 3) + 1 }))}
              />
            </PhasePanel>

            {/* Root Cause (manual textarea) */}
            <Field label="Root Cause" hint="Summarise the confirmed root cause from the whys above">
              <Textarea rows={3} value={form.root_cause || ""}
                onChange={e => setForm(f => ({ ...f, root_cause: e.target.value }))}
                placeholder="Type the confirmed root cause here…"
                data-testid="cc-root-cause" />
            </Field>

            {/* ── Corrective Action (checklist) + Preventive Action ── */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <CorrectiveActionChecklist
                steps={form.corrective_action_steps}
                legacyText={typeof form.corrective_action === "string" ? form.corrective_action : ""}
                onChange={(next) => setForm(f => ({ ...f, corrective_action_steps: next, corrective_action: "" }))}
              />
              <Field label="Preventive Action">
                <Textarea rows={3} value={form.preventive_action}
                  onChange={e => setForm(f => ({ ...f, preventive_action: e.target.value }))}
                  data-testid="cc-preventive" placeholder="What was put in place to prevent recurrence…" />
              </Field>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#1D2951] hover:bg-[#2A3B73]" data-testid="cc-save">
              {editingId ? "Save Changes" : "Create Complaint"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Add Probable Cause modal ── */}
      <Dialog open={addCauseOpen} onOpenChange={setAddCauseOpen}>
        <DialogContent className="max-w-md" data-testid="cc-add-cause-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-black text-[#1D2951]">
              Add Probable Cause
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Field label="Category">
              <Select value={addCauseCat} onValueChange={setAddCauseCat}>
                <SelectTrigger className="h-9" data-testid="cc-add-cause-cat"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FISHBONE_CATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Probable Cause">
              <Input value={addCauseText}
                onChange={e => setAddCauseText(e.target.value)}
                placeholder="e.g. Operator fatigue, Wrong tool used, …"
                className="h-9" data-testid="cc-add-cause-text" autoFocus />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddCauseOpen(false)}>Cancel</Button>
            <Button onClick={addProbableCause} className="bg-[#1D2951] hover:bg-[#2A3B73]" data-testid="cc-add-cause-save">
              Save Cause
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Viewer dialog ── */}
      <Dialog open={viewerOpen} onOpenChange={setViewerOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="cc-viewer">
          <DialogHeader>
            <DialogTitle className="text-xl font-black text-[#1D2951] flex items-center gap-3">
              Complaint · {viewing?.part_no || ""} · {viewing?.complaint_date || ""}
              {viewing?.complaint_no && (
                <span className="text-[11px] font-mono font-bold px-2 py-1 rounded bg-[#D4A843] text-[#1D2951]">
                  {viewing.complaint_no}
                </span>
              )}
              {viewing?.status && (
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${STATUS_STYLE[viewing.status] || "bg-[#F1F5F9] text-[#64748B]"}`}>
                  {viewing.status}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          {viewing && <ComplaintViewer data={viewing} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewerOpen(false)}>Close</Button>
            {viewing && <Button onClick={() => doPrint(viewing.id)} className="bg-[#1D2951] hover:bg-[#2A3B73]">
              <Printer size={14} className="mr-1.5" /> Print
            </Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

/* ─────────────────── Sub-components ─────────────────── */

function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-[11px] font-bold uppercase tracking-widest text-[#64748B]">{label}</Label>
      {hint && <p className="text-[10px] text-[#94A3B8] mb-1">{hint}</p>}
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

function IconBtn({ children, onClick, title, testId, danger }) {
  return (
    <button onClick={onClick} title={title} data-testid={testId}
      className={`w-8 h-8 flex items-center justify-center rounded-md border transition
        ${danger ? "border-[#FCA5A5] text-[#B91C1C] hover:bg-[#FEE2E2]"
                 : "border-[#E2E8F0] text-[#334155] hover:border-[#1D2951] hover:text-[#1D2951]"}`}>
      {children}
    </button>
  );
}

function SectionDivider({ label }) {
  return (
    <div className="pt-4 mt-4 border-t-2 border-[#1D2951]">
      <span className="text-[10px] font-black uppercase tracking-widest text-white bg-[#1D2951] px-2 py-0.5 rounded">
        {label}
      </span>
    </div>
  );
}

function PhasePanel({ code, title, accent = "#7C3AED", children }) {
  return (
    <div className="p-4 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] font-black text-white px-2 py-0.5 rounded" style={{ backgroundColor: accent }}>
          {code}
        </span>
        <h4 className="text-sm font-bold text-[#1D2951]">{title}</h4>
      </div>
      {children}
    </div>
  );
}

/* ── Fishbone (Ishikawa) diagram — SVG spine + click-to-add branches ── */
function FishboneDiagram({ categories, causesByCategory, onAddCause }) {
  // 6 categories split 3 top / 3 bottom (classic layout).
  const top    = categories.slice(0, 3);
  const bottom = categories.slice(3);

  return (
    <div className="w-full">
      <div className="relative w-full overflow-x-auto">
        <div className="relative min-w-[720px] py-4">
          {/* Top row (branches slanting down-right) */}
          <div className="grid grid-cols-3 gap-4">
            {top.map(cat => (
              <BranchCard key={cat} cat={cat} causes={causesByCategory[cat] || []}
                onAdd={() => onAddCause(cat)} orient="top" />
            ))}
          </div>
          {/* Spine (fish backbone) */}
          <div className="relative my-4 mx-6 h-[52px]">
            <svg viewBox="0 0 800 52" className="w-full h-full" preserveAspectRatio="none">
              {/* Body line */}
              <line x1="0" y1="26" x2="740" y2="26" stroke="#1D2951" strokeWidth="3" />
              {/* Fish head triangle */}
              <polygon points="740,26 800,6 800,46" fill="#1D2951" />
              {/* Vertical ticks where branches meet the spine */}
              {[0.16, 0.36, 0.56, 0.24, 0.44, 0.64].map((x, i) => (
                <line key={i} x1={x * 740} y1="26" x2={x * 740} y2={i < 3 ? "8" : "44"}
                  stroke="#CBD5E1" strokeWidth="1" strokeDasharray="2,2" />
              ))}
            </svg>
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-white text-[10px] font-black tracking-widest pr-3">
              PROBLEM
            </span>
          </div>
          {/* Bottom row (branches slanting up-right) */}
          <div className="grid grid-cols-3 gap-4">
            {bottom.map(cat => (
              <BranchCard key={cat} cat={cat} causes={causesByCategory[cat] || []}
                onAdd={() => onAddCause(cat)} orient="bottom" />
            ))}
          </div>
        </div>
      </div>
      <p className="text-[10px] text-[#94A3B8] mt-2">
        Click <b>+ Add cause</b> on any branch to enrich the probable-cause master. Added causes flow into the analysis table below.
      </p>
    </div>
  );
}

function BranchCard({ cat, causes, onAdd, orient }) {
  const accent = {
    Man: "#DC2626", Material: "#059669", Machine: "#2563EB",
    Method: "#7C3AED", Tool: "#EA580C", Environment: "#0891B2",
  }[cat] || "#1D2951";
  return (
    <div className={`p-3 rounded-lg bg-white border-2 shadow-sm ${orient === "top" ? "rounded-b-none border-b-0" : "rounded-t-none border-t-0"}`}
      style={{ borderColor: accent }}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Fish size={14} weight="fill" style={{ color: accent }} />
          <span className="text-xs font-black uppercase tracking-wider" style={{ color: accent }}>{cat}</span>
        </div>
        <button type="button" onClick={onAdd}
          className="text-[10px] font-bold px-2 py-0.5 rounded border border-[#E2E8F0] text-[#334155] hover:bg-[#F1F5F9]"
          data-testid={`cc-fish-add-${cat.toLowerCase()}`}>
          + Add cause
        </button>
      </div>
      <div className="space-y-1 max-h-20 overflow-y-auto">
        {causes.length === 0 ? (
          <p className="text-[10px] text-[#94A3B8] italic">No causes yet</p>
        ) : (
          causes.map(c => (
            <div key={c.id} className="text-[11px] text-[#334155] truncate" title={c.cause}>
              • {c.cause}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/* ── Cause Analysis table ──
 * Rows are AUTO-POPULATED from every probable cause defined in the master
 * (grouped by category). User picks Yes/No per row — Effect is derived
 * (Yes → Effective, No → Not effective).
 */
function CauseAnalysisTable({ rows, causesByCategory, onChange }) {
  // Flatten every probable cause into the ordered rows list.
  const flatCauses = useMemo(() => {
    const out = [];
    for (const cat of FISHBONE_CATS) {
      for (const c of (causesByCategory[cat] || [])) {
        out.push({ category: cat, probable_cause: c.cause });
      }
    }
    return out;
  }, [causesByCategory]);

  // Sync `rows` with the master list, preserving any user-picked yes_no.
  useEffect(() => {
    const existing = new Map((rows || []).map(r =>
      [`${r.category}|${r.probable_cause}`, r]));
    const next = flatCauses.map(fc => {
      const key = `${fc.category}|${fc.probable_cause}`;
      const cur = existing.get(key) || {};
      return {
        category: fc.category,
        probable_cause: fc.probable_cause,
        yes_no: cur.yes_no || "",
      };
    });
    const prevKey = (rows || []).map(r => `${r.category}|${r.probable_cause}|${r.yes_no || ""}`).join(",");
    const nextKey = next.map(r => `${r.category}|${r.probable_cause}|${r.yes_no || ""}`).join(",");
    if (prevKey !== nextKey) onChange(next);
  }, [flatCauses, rows, onChange]);

  const setYesNo = (i, val) => {
    const next = rows.map((r, idx) => idx === i ? { ...r, yes_no: val } : r);
    onChange(next);
  };

  if (flatCauses.length === 0) {
    return (
      <p className="text-[11px] text-[#94A3B8] italic">
        No probable causes defined yet. Use the <b>+ Add cause</b> buttons on the Fishbone diagram above to add causes; they will appear here automatically.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-[#E2E8F0]">
      <table className="w-full text-xs">
        <thead className="bg-[#F1F5F9] text-[#1D2951]">
          <tr>
            <th className="px-2 py-2 text-left font-semibold min-w-[130px]">Category</th>
            <th className="px-2 py-2 text-left font-semibold min-w-[280px]">Probable Cause</th>
            <th className="px-2 py-2 text-left font-semibold min-w-[130px]">Note</th>
            <th className="px-2 py-2 text-left font-semibold min-w-[150px]">Effect</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#F1F5F9]">
          {rows.map((r, i) => {
            const derived = r.yes_no === "Yes" ? "Effective"
                          : r.yes_no === "No"  ? "Not effective" : "—";
            const chip = r.yes_no === "Yes"
              ? "bg-[#DCFCE7] text-[#166534]"
              : r.yes_no === "No"
                ? "bg-[#FEE2E2] text-[#B91C1C]"
                : "text-[#94A3B8]";
            return (
              <tr key={`${r.category}-${r.probable_cause}-${i}`}>
                <td className="p-1 font-semibold text-[#334155]">{r.category}</td>
                <td className="p-1 text-[#0F172A]">{r.probable_cause}</td>
                <td className="p-1">
                  <Select value={r.yes_no || ""} onValueChange={v => setYesNo(i, v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`cc-ca-yn-${i}`}>
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Yes">Yes</SelectItem>
                      <SelectItem value="No">No</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="p-1">
                  <span className={`text-[11px] font-bold px-2 py-1 rounded ${chip}`} data-testid={`cc-ca-effect-${i}`}>
                    {derived}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ── Root Cause Analysis (3-Whys) table ──
 * Auto-materialises one row per cause marked "Yes" in the Cause Analysis
 * table above. Three Why columns per row. Existing user text is
 * preserved when the responsible list changes.
 */
function WhyAnalysisTable({ responsibleCauses, rows, whyCount, onChange, onAddColumn }) {
  useEffect(() => {
    const existing = new Map((rows || []).map(r => [`${r.category}|${r.probable_cause}`, r]));
    const next = responsibleCauses.map(rc => {
      const key = `${rc.category}|${rc.probable_cause}`;
      const cur = existing.get(key);
      // Preserve/extend the whys array to match the current column count.
      const arr = Array.from({ length: whyCount }, (_, k) => (cur?.whys || [])[k] || "");
      return { category: rc.category, probable_cause: rc.probable_cause, whys: arr };
    });
    const prevKey = (rows || []).map(r => `${r.category}|${r.probable_cause}|${(r.whys || []).join("¶")}`).join(",");
    const nextKey = next.map(r => `${r.category}|${r.probable_cause}|${r.whys.join("¶")}`).join(",");
    if (prevKey !== nextKey) onChange(next);
  }, [responsibleCauses.map(r => `${r.category}|${r.probable_cause}`).join(","), rows, whyCount, onChange]);

  const setWhy = (i, w, val) => {
    const next = rows.map((r, idx) => {
      if (idx !== i) return r;
      const whys = Array.from({ length: whyCount }, (_, k) => (r.whys || [])[k] || "");
      whys[w] = val;
      return { ...r, whys };
    });
    onChange(next);
  };

  if (rows.length === 0) {
    return (
      <p className="text-[11px] text-[#94A3B8] italic">
        Mark at least one row as <b>Yes</b> (Effective) in the Cause Analysis table above to populate the Whys.
      </p>
    );
  }

  const whyIdxs = Array.from({ length: whyCount }, (_, k) => k);

  return (
    <div className="overflow-x-auto rounded-lg border border-[#E2E8F0]">
      <table className="w-full text-xs">
        <thead className="bg-[#F1F5F9] text-[#1D2951]">
          <tr>
            <th className="px-2 py-2 text-left font-semibold min-w-[110px]">Category</th>
            <th className="px-2 py-2 text-left font-semibold min-w-[220px]">Probable Cause</th>
            {whyIdxs.map(w => (
              <th key={w} className="px-2 py-2 text-left font-semibold min-w-[260px]">Why {w + 1}</th>
            ))}
            <th className="px-2 py-2 text-center font-semibold w-[120px]">
              <button type="button" onClick={onAddColumn}
                className="text-[10px] font-bold px-2 py-1 rounded border border-[#1D2951] text-[#1D2951] bg-white hover:bg-[#1D2951] hover:text-white transition"
                data-testid="cc-why-add-col">
                + Add Column
              </button>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#F1F5F9]">
          {rows.map((r, i) => (
            <tr key={`${r.category}-${r.probable_cause}-${i}`}>
              <td className="p-2 font-semibold text-[#334155] align-top">{r.category}</td>
              <td className="p-2 text-[#0F172A] align-top">{r.probable_cause}</td>
              {whyIdxs.map(w => (
                <td key={w} className="p-1 align-top">
                  <Textarea value={(r.whys || [])[w] || ""}
                    onChange={e => setWhy(i, w, e.target.value)}
                    rows={3}
                    className="text-xs resize-y min-h-[70px]"
                    placeholder={`Why #${w + 1}`}
                    data-testid={`cc-why-${i}-${w}`} />
                </td>
              ))}
              <td className="p-1"></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Corrective Action Checklist ──
 * Each step = {text, done}. Header shows progress badge (e.g. "2/5 done").
 * A legacy single-string `corrective_action` on old records is converted
 * to a first step on the fly so nothing gets lost.
 */
function CorrectiveActionChecklist({ steps, legacyText, onChange }) {
  const initialised = useMemo(() => {
    if (Array.isArray(steps) && steps.length > 0) return steps;
    if (legacyText && legacyText.trim()) return [{ text: legacyText.trim(), done: false }];
    return [{ text: "", done: false }];
  }, [steps, legacyText]);

  // Ensure parent has a normalised array (one-time when loading legacy text).
  useEffect(() => {
    if (!Array.isArray(steps) || steps.length === 0) {
      if (legacyText && legacyText.trim()) onChange([{ text: legacyText.trim(), done: false }]);
    }
  }, [steps, legacyText, onChange]);

  const list = initialised;
  const setStep = (i, patch) => {
    const next = list.map((s, idx) => idx === i ? { ...s, ...patch } : s);
    onChange(next);
  };
  const addStep = () => onChange([...list, { text: "", done: false }]);
  const rmStep  = (i) => onChange(list.filter((_, j) => j !== i));

  const total = list.filter(s => (s.text || "").trim()).length;
  const done  = list.filter(s => s.done && (s.text || "").trim()).length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const statusChip =
    pct === 100 && total > 0 ? "bg-[#DCFCE7] text-[#166534]" :
    pct > 0                  ? "bg-[#FEF3C7] text-[#B45309]" :
                               "bg-[#F1F5F9] text-[#64748B]";
  const statusLabel = total === 0 ? "No steps" :
                      pct === 100 ? "Completed" :
                      pct > 0     ? "In progress" : "Not started";

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-[11px] font-bold uppercase tracking-widest text-[#64748B]">
          Corrective Action
        </Label>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${statusChip}`}
                data-testid="cc-corrective-status">
            {statusLabel} · {done}/{total}
          </span>
        </div>
      </div>
      {total > 0 && (
        <div className="h-1.5 bg-[#F1F5F9] rounded-full overflow-hidden mb-2" data-testid="cc-corrective-progress">
          <div className="h-full bg-gradient-to-r from-[#1D2951] to-[#D4A843] transition-all"
               style={{ width: `${pct}%` }} />
        </div>
      )}
      <div className="space-y-1.5">
        {list.map((s, i) => (
          <div key={i} className="flex items-start gap-2 p-2 rounded-md bg-[#F8FAFC] border border-[#E2E8F0]">
            <input type="checkbox" checked={!!s.done}
              onChange={e => setStep(i, { done: e.target.checked })}
              className="mt-1.5 w-4 h-4 accent-[#1D2951] cursor-pointer"
              data-testid={`cc-corr-done-${i}`} />
            <Textarea value={s.text || ""}
              onChange={e => setStep(i, { text: e.target.value })}
              rows={2}
              placeholder={`Corrective step #${i + 1}…`}
              className={`flex-1 text-xs resize-y min-h-[44px] ${s.done ? "line-through text-[#94A3B8]" : ""}`}
              data-testid={`cc-corr-text-${i}`} />
            {list.length > 1 && (
              <button type="button" onClick={() => rmStep(i)}
                className="mt-1.5 text-[#B91C1C] hover:text-[#7F1D1D]"
                title="Remove step"
                data-testid={`cc-corr-rm-${i}`}>
                <X size={14} weight="bold" />
              </button>
            )}
          </div>
        ))}
      </div>
      <Button type="button" variant="outline" size="sm" onClick={addStep}
        className="mt-2 h-8" data-testid="cc-corr-add">
        <Plus size={12} className="mr-1" /> Add next step
      </Button>
    </div>
  );
}

/* ── Viewer (read-only) ── */
function ComplaintViewer({ data }) {
  return (
    <div className="space-y-4">
      <ViewGrid data={[
        ["Complaint No",  data.complaint_no],
        ["Status",        data.status],
        ["Complaint Date", data.complaint_date],
        ["Invoice Date",  data.invoice_date],
        ["Part No",       data.part_no],
        ["Part Description", data.part_description],
        ["Drawing No",    data.drawing_no],
        ["Rev No",        data.rev_no],
        ["Customer",      data.customer],
        ["Lot Qty",       data.lot_qty],
        ["Rejected Qty",  data.rejected_qty],
        ["Rework Qty",    data.rework_qty],
        ["Replaced Qty",  data.replaced_qty],
        ["Customer Rep",  data.customer_rep_name],
        ["Defect",        data.defect],
        ["Type of Defect", data.defect_type],
        ["Process Name",  data.process_name],
        ["Process Owner", data.process_owner],
        ["Quality Supervisor", data.quality_supervisor],
        ["Operator Supervisor", data.operator_supervisor],
        ["Inspector Name", data.inspector_name],
        ["Defect Locations", (data.defect_locations || []).join(", ")],
      ]} />
      {data.complaint_statement && (
        <div>
          <div className="text-[10px] uppercase font-bold tracking-widest text-[#64748B]">Complaint Statement</div>
          <div className="mt-1 p-3 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] text-sm whitespace-pre-wrap">{data.complaint_statement}</div>
        </div>
      )}
      {(data.cause_analysis || []).length > 0 && (
        <div>
          <div className="text-[10px] uppercase font-bold tracking-widest text-[#64748B] mb-1">Cause Analysis</div>
          <div className="overflow-x-auto rounded border border-[#E2E8F0]">
            <table className="w-full text-xs">
              <thead className="bg-[#F1F5F9]"><tr>
                <th className="px-2 py-1.5 text-left">Category</th>
                <th className="px-2 py-1.5 text-left">Probable Cause</th>
                <th className="px-2 py-1.5 text-left">Note</th>
                <th className="px-2 py-1.5 text-left">Effect</th>
              </tr></thead>
              <tbody>
                {data.cause_analysis.map((r, i) => {
                  const eff = r.yes_no === "Yes" ? "Effective"
                            : r.yes_no === "No"  ? "Not effective" : "—";
                  return (
                    <tr key={i} className="border-t border-[#F1F5F9]">
                      <td className="px-2 py-1">{r.category}</td>
                      <td className="px-2 py-1">{r.probable_cause}</td>
                      <td className="px-2 py-1">{r.yes_no || "—"}</td>
                      <td className="px-2 py-1 font-semibold">{eff}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {(data.why_analysis || []).length > 0 && (() => {
        const maxWhy = Math.max(...data.why_analysis.map(r => (r.whys || []).length), 3);
        return (
          <div>
            <div className="text-[10px] uppercase font-bold tracking-widest text-[#64748B] mb-1">Root Cause Analysis (Whys)</div>
            <div className="overflow-x-auto rounded border border-[#E2E8F0]">
              <table className="w-full text-xs">
                <thead className="bg-[#F1F5F9]"><tr>
                  <th className="px-2 py-1.5 text-left">Category</th>
                  <th className="px-2 py-1.5 text-left">Probable Cause</th>
                  {Array.from({ length: maxWhy }, (_, k) => (
                    <th key={k} className="px-2 py-1.5 text-left">Why {k + 1}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {data.why_analysis.map((r, i) => (
                    <tr key={i} className="border-t border-[#F1F5F9]">
                      <td className="px-2 py-1">{r.category}</td>
                      <td className="px-2 py-1">{r.probable_cause}</td>
                      {Array.from({ length: maxWhy }, (_, k) => (
                        <td key={k} className="px-2 py-1 whitespace-pre-wrap">{(r.whys || [])[k] || "—"}</td>
                      ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        );
      })()}
      {(data.corrective_action_steps || data.corrective_action || data.preventive_action || data.root_cause) && (
        <div className="space-y-3">
          {data.root_cause && (
            <div>
              <div className="text-[10px] uppercase font-bold tracking-widest text-[#64748B]">Root Cause</div>
              <div className="mt-1 p-2 rounded bg-[#F8FAFC] border border-[#E2E8F0] text-sm whitespace-pre-wrap">{data.root_cause}</div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(data.corrective_action_steps || []).length > 0 ? (
              <div>
                <div className="text-[10px] uppercase font-bold tracking-widest text-[#64748B] mb-1">
                  Corrective Action ({data.corrective_action_steps.filter(s => s.done).length}/{data.corrective_action_steps.length} done)
                </div>
                <ul className="space-y-1">
                  {data.corrective_action_steps.map((s, i) => (
                    <li key={i} className="text-sm flex gap-2">
                      <span className={`mt-0.5 w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${s.done ? "bg-[#166534] border-[#166534] text-white" : "border-[#CBD5E1]"}`}>
                        {s.done && "✓"}
                      </span>
                      <span className={s.done ? "line-through text-[#94A3B8]" : "text-[#0F172A]"}>{s.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : data.corrective_action ? (
              <div>
                <div className="text-[10px] uppercase font-bold tracking-widest text-[#64748B]">Corrective Action</div>
                <div className="mt-1 p-2 rounded bg-[#F8FAFC] border border-[#E2E8F0] text-sm whitespace-pre-wrap">{data.corrective_action}</div>
              </div>
            ) : null}
            {data.preventive_action && (
              <div>
                <div className="text-[10px] uppercase font-bold tracking-widest text-[#64748B]">Preventive Action</div>
                <div className="mt-1 p-2 rounded bg-[#F8FAFC] border border-[#E2E8F0] text-sm whitespace-pre-wrap">{data.preventive_action}</div>
              </div>
            )}
          </div>
        </div>
      )}
      {data.ica_notes && (
        <div>
          <div className="text-[10px] uppercase font-bold tracking-widest text-[#64748B]">D3 · ICA Notes</div>
          <div className="mt-1 p-2 rounded bg-[#F8FAFC] border border-[#E2E8F0] text-sm whitespace-pre-wrap">{data.ica_notes}</div>
        </div>
      )}
      {(data.photos || []).length > 0 && (
        <div>
          <div className="text-[10px] uppercase font-bold tracking-widest text-[#64748B] mb-2">Photos</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {data.photos.map((p, i) => (
              <a key={i} href={p.data_url} target="_blank" rel="noreferrer" className="block w-full aspect-square rounded-lg overflow-hidden border border-[#E2E8F0]">
                <img src={p.data_url} alt={p.name} className="w-full h-full object-cover hover:scale-105 transition" />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ViewGrid({ data }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {data.map(([k, v], i) => (
        <div key={i}>
          <div className="text-[10px] uppercase font-bold tracking-widest text-[#64748B]">{k}</div>
          <div className="mt-0.5 text-sm font-mono text-[#0F172A]">
            {(v ?? "") === "" ? <span className="text-[#94A3B8]">—</span> : String(v)}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Printable window ── */
function renderPrintWindow(doc) {
  const w = window.open("", "_blank", "width=900,height=1100");
  if (!w) { toast.error("Popup blocked — please allow popups to print"); return; }
  const esc = (s) => String(s ?? "").replace(/[&<>]/g, c => ({ "&":"&amp;", "<":"&lt;", ">":"&gt;" })[c]);
  const row = (label, val) =>
    `<div class="row"><span class="lbl">${esc(label)}</span><span class="val">${esc(val || "—")}</span></div>`;
  const photos = (doc.photos || []).map(p =>
    `<img src="${esc(p.data_url)}" alt="${esc(p.name)}" />`).join("");
  const box = (label, val) => val
    ? `<div class="box-label">${esc(label)}</div><div class="box">${esc(val)}</div>` : "";
  const caTable = (doc.cause_analysis || []).length > 0 ? `
    <div class="box-label">Cause Analysis</div>
    <table class="tbl"><thead><tr>
      <th>Category</th><th>Probable Cause</th><th>Note</th><th>Effect</th>
    </tr></thead><tbody>
    ${doc.cause_analysis.map(r => {
      const eff = r.yes_no === "Yes" ? "Effective"
                : r.yes_no === "No"  ? "Not effective" : "—";
      return `<tr>
        <td>${esc(r.category)}</td><td>${esc(r.probable_cause)}</td>
        <td>${esc(r.yes_no || "—")}</td><td>${esc(eff)}</td>
      </tr>`;
    }).join("")}
    </tbody></table>
  ` : "";
  const maxWhy = (doc.why_analysis || []).length > 0
    ? Math.max(...doc.why_analysis.map(r => (r.whys || []).length), 3) : 3;
  const whyHeaders = Array.from({ length: maxWhy }, (_, k) => `<th>Why ${k + 1}</th>`).join("");
  const whyBlocks = (doc.why_analysis || []).length > 0 ? `
    <div class="box-label">Root Cause Analysis (Whys)</div>
    <table class="tbl"><thead><tr>
      <th>Category</th><th>Probable Cause</th>${whyHeaders}
    </tr></thead><tbody>
    ${doc.why_analysis.map(r => `<tr>
      <td>${esc(r.category)}</td><td>${esc(r.probable_cause)}</td>
      ${Array.from({ length: maxWhy }, (_, k) => `<td>${esc((r.whys || [])[k] || "—")}</td>`).join("")}
    </tr>`).join("")}
    </tbody></table>
  ` : "";
  const correctiveBlock = (doc.corrective_action_steps || []).length > 0
    ? `<div class="box-label">Corrective Action (${(doc.corrective_action_steps || []).filter(s => s.done).length}/${doc.corrective_action_steps.length} done)</div>
       <ul style="list-style:none;padding-left:0;font-size:12px;">
         ${doc.corrective_action_steps.map(s =>
           `<li style="margin:4px 0;"><span style="display:inline-block;width:14px;height:14px;border:2px solid ${s.done ? "#166534" : "#CBD5E1"};background:${s.done ? "#166534" : "transparent"};color:white;text-align:center;line-height:10px;font-size:10px;border-radius:2px;margin-right:6px;vertical-align:middle;">${s.done ? "✓" : ""}</span><span style="${s.done ? "text-decoration:line-through;color:#94A3B8;" : ""}">${esc(s.text)}</span></li>`
         ).join("")}
       </ul>`
    : (doc.corrective_action ? box("Corrective Action", doc.corrective_action) : "");
  w.document.write(`<!doctype html><html><head><title>Complaint · ${esc(doc.complaint_no || doc.part_no || "")}</title>
    <style>
      * { box-sizing: border-box; }
      body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #0F172A; margin: 32px; }
      h1 { font-size: 22px; margin: 0 0 4px; color: #1D2951; letter-spacing: -0.02em; }
      .sub { color: #64748B; font-size: 12px; margin-bottom: 18px; }
      .badge { display: inline-block; background: #D4A843; color: #1D2951; font-family: monospace;
               font-weight: bold; padding: 2px 8px; border-radius: 4px; font-size: 11px; margin-right: 6px; }
      .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 24px; margin-bottom: 14px; }
      .row { display: flex; justify-content: space-between; gap: 12px; border-bottom: 1px dotted #CBD5E1; padding: 6px 0; }
      .lbl { color: #64748B; font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700; }
      .val { color: #0F172A; font-family: monospace; font-size: 12px; text-align: right; max-width: 60%; word-break: break-word; }
      .box { background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 6px; padding: 10px; font-size: 12px; white-space: pre-wrap; margin-bottom: 8px; }
      .box-label { color: #64748B; font-size: 10px; text-transform: uppercase; letter-spacing: 0.08em; font-weight: 800; margin: 10px 0 4px; }
      .tbl { width: 100%; border-collapse: collapse; font-size: 11px; }
      .tbl th, .tbl td { border: 1px solid #E2E8F0; padding: 4px 6px; text-align: left; }
      .tbl thead { background: #F1F5F9; }
      .photos { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; }
      .photos img { width: 100%; height: 220px; object-fit: cover; border-radius: 6px; border: 1px solid #E2E8F0; page-break-inside: avoid; }
      h2 { font-size: 14px; margin: 14px 0 6px; color: #1D2951; border-top: 2px solid #1D2951; padding-top: 10px; }
    </style></head><body>
    <h1>Customer Complaint</h1>
    <div class="sub">
      ${doc.complaint_no ? `<span class="badge">${esc(doc.complaint_no)}</span>` : ""}
      ${esc(doc.part_no || "")} · ${esc(doc.customer || "")} · ${esc(doc.complaint_date || "")}
      ${doc.status ? ` · ${esc(doc.status)}` : ""}
    </div>
    <div class="grid">
      ${row("Complaint Date", doc.complaint_date)}
      ${row("Invoice Date",   doc.invoice_date)}
      ${row("Drawing No",     doc.drawing_no)}
      ${row("Rev No",         doc.rev_no)}
      ${row("Part No",        doc.part_no)}
      ${row("Part Description", doc.part_description)}
      ${row("Customer",       doc.customer)}
      ${row("Lot Qty",        doc.lot_qty)}
      ${row("Rejected Qty",   doc.rejected_qty)}
      ${row("Rework Qty",     doc.rework_qty)}
      ${row("Replaced Qty",   doc.replaced_qty)}
      ${row("Customer Rep",   doc.customer_rep_name)}
      ${row("Defect",         doc.defect)}
      ${row("Type of Defect", doc.defect_type)}
      ${row("Process Name",   doc.process_name)}
      ${row("Process Owner",  doc.process_owner)}
      ${row("Quality Supervisor", doc.quality_supervisor)}
      ${row("Operator Supervisor", doc.operator_supervisor)}
      ${row("Inspector Name", doc.inspector_name)}
      ${row("Defect Locations", (doc.defect_locations || []).join(", "))}
    </div>
    ${box("Complaint Statement", doc.complaint_statement)}
    <h2>CAPA · 8D</h2>
    ${box("CAPA Notes", doc.capa_notes)}
    ${(doc.team_members || []).length > 0 ? `<div class="box-label">D1 · Team</div><div class="box">${doc.team_members.map(m => esc(m)).join(", ")}</div>` : ""}
    ${(doc.problem_what || doc.problem_where || doc.problem_when || doc.problem_who || doc.problem_how || doc.problem_many) ? `
      <div class="box-label">D2 · Problem Definition</div>
      <div class="grid">
        ${row("What",  doc.problem_what)}
        ${row("Where", doc.problem_where)}
        ${row("When",  doc.problem_when)}
        ${row("Who",   doc.problem_who)}
        ${row("How",   doc.problem_how)}
        ${row("Many",  doc.problem_many)}
      </div>
    ` : ""}
    ${caTable}
    ${whyBlocks}
    ${box("Root Cause", doc.root_cause)}
    ${correctiveBlock}
    ${box("Preventive Action", doc.preventive_action)}
    ${box("D3 · ICA Notes", doc.ica_notes)}
    ${(doc.photos || []).length > 0
      ? `<h2>Photos of Defect</h2><div class="photos">${photos}</div>` : ""}
    <script>window.onload = () => setTimeout(() => window.print(), 250);</script>
    </body></html>`);
  w.document.close();
}
